// ═══════════════════════════════════════════════════════════════════════════
// AI → EditorCommand translator — DRAW-MODIFY domain (CC-2c sub-arm 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// Translates the draw-modify AI command shapes (TRIM/EXTEND/OFFSET/MIRROR/
// SCALE/STRETCH/ARRAYRECT/ARRAYEDIT) into the internal `EditorCommand`s the
// store dispatches. Every path runs the CC-2a fail-closed guard
// (`assertAiCallableDrawAction`) BEFORE producing any EditorCommand, so the
// safe contour rejects a non-AI-callable / unknown target without mutating the
// document. Geometry is only ever changed by the reducer the returned commands
// flow into — never written here — honouring the CLAUDE.md rules:
//   "AI drafting … может только предлагать и исполнять команды через
//    безопасный контур." + "любые изменения геометрии только через Command
//    Engine."
//
// The thin per-action translators live in the PACKAGE-INTERNAL sibling
// `drawModifyTranslators.ts` (NOT re-exported by the barrel), so the ONLY public
// draw-modify entry is the guarded `translateDrawModifyCommand` below. This
// mirrors the BLOCK domain (per-action translators in non-barrelled
// `blockDispatcher*.ts`), preventing a consumer of `@dxf-ai-editor/ai-commands`
// from emitting a geometry command past the fail-closed guard.

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
} from "@dxf-ai-editor/command-registry";

import {
  normalizeDispatcherArgs,
  type AiDispatchContext,
} from "./blockDispatcherShared";
import { assertAiCallableDrawAction } from "./drawModifyGuard";
import type { DrawModifyAiCommand } from "./drawModifySchemas";
import {
  translateArrayEdit,
  translateArrayRect,
  translateExtend,
  translateMirror,
  translateOffset,
  translateScale,
  translateStretch,
  translateTrim,
} from "./drawModifyTranslators";

export {
  DRAW_MODIFY_AI_ACTION_TARGET_COMMAND,
  assertAiCallableDrawAction,
} from "./drawModifyGuard";

/**
 * Top-level draw-modify dispatcher. Runs the CC-2a fail-closed guard, then
 * routes the AI command to the translator that owns its `action`. Returns the
 * flat `EditorCommand[]` the store should dispatch (the caller wraps the batch
 * in one grouped-undo unit — see the app safe contour).
 *
 * `context` carries the live editor state the translators need. Today only
 * MIRROR consumes it (`context.mirrtext` — the live MIRRTEXT sysvar stamped
 * when the proposal omits its own value, Codex #342 F2); the other draw-modify
 * actions are fully self-contained (they reference entity ids directly).
 */
export function translateDrawModifyCommand(
  command: DrawModifyAiCommand,
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  // Normalise a legacy `(command, descriptorArray)` call so a descriptor array
  // passed as the 2nd arg is treated as `descriptors`, not `context` — keeping
  // the fail-closed guard honoured for restricted registries (shared one-truth
  // `normalizeDispatcherArgs`; #342 F5, mirrors viewSpaceDispatcher).
  ({ context, descriptors } = normalizeDispatcherArgs(context, descriptors));
  // Safe-contour gate (CC-2a): refuse to translate any draw-modify action whose
  // target command is not `aiCallable: true`. Fails closed before any
  // EditorCommand is produced. `descriptors` is injectable for tests.
  assertAiCallableDrawAction(command.action, descriptors);
  switch (command.action) {
    case "TRIM":
      return translateTrim(command);
    case "EXTEND":
      return translateExtend(command);
    case "OFFSET":
      return translateOffset(command);
    case "MIRROR":
      return translateMirror(command, context);
    case "SCALE":
      return translateScale(command);
    case "STRETCH":
      return translateStretch(command);
    case "ARRAYRECT":
      return translateArrayRect(command);
    case "ARRAYEDIT":
      return translateArrayEdit(command);
  }
}
